import { Client } from 'api/client'
import axios, { AxiosResponse, CancelTokenSource } from 'axios'
import {
  IInboxFilters,
  IInboxPatchModel,
  IInboxPatchSingle,
  InboxSectionEnum,
} from 'Modules/Communication/shared/models/Inbox'
import { convertEnumToReadable } from 'Modules/Communication/shared/util/sectionHelper'
import {
  getSmsMessages,
  getSms,
  patchSms,
  deleteSms,
  deleteSmsList,
} from 'Modules/Communication/Sms/shared/api/sms.api'
import {
  ISmsListItem,
  ISmsModel,
} from 'Modules/Communication/Sms/shared/models/Sms'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import { ICommonListModel } from 'shared/models/Common'
import { navigate } from 'shared/routing'
import { getActiveClient } from 'shared/store/clients/selectors'
import { closeModal } from 'shared/store/modal'
import { addToasterMessage } from 'shared/store/toaster'
import {
  SMS_DELETESTATUS_RESET,
  SMS_DELETE_ERROR,
  SMS_DELETE_REQUESTED,
  SMS_DELETE_SUCCESS,
  SMS_FETCH_MORE_REQUESTED,
  SMS_FETCH_MORE_REQUEST_ERROR,
  SMS_FETCH_MORE_REQUEST_SUCCESS,
  SMS_FETCH_NEW_SILENTLY_REQUESTED,
  SMS_FETCH_NEW_SILENTLY_SUCCESS,
  SMS_FETCH_REQUEST_ERROR,
  SMS_FETCH_REQUEST_SUCCESS,
  SMS_FETCH_SILENT_ERROR,
  SMS_FETCH_SILENT_REQUESTED,
  SMS_FETCH_SILENT_SUCCESS,
  SMS_MESSAGES_FETCH_REQUESTED,
  SMS_MESSAGES_FETCH_REQUEST_ERROR,
  SMS_MESSAGES_FETCH_REQUEST_SUCCESS,
  SMS_REMOVE_HANDLED_BY_REQUESTED,
  SMS_REMOVE_HANDLED_BY_SUCCESS,
  SMS_RESET_CURRENT,
  SMS_RESTORE_REQUESTED,
  SMS_RESTORE_SUCCESS,
  SMS_SET_CURRENT,
  SMS_SET_FILTERS,
  SMS_SET_HANDLED_BY_REQUESTED,
  SMS_SET_HANDLED_BY_SUCCESS,
} from './actionTypes'
import { getCurrentSms, getSmsFilters, getSmsSection } from './selectors'
import { SmsActionTypes } from './types'

function* fetchSmsMessages(action: SmsActionTypes) {
  try {
    const activeClient: Client = yield select(getActiveClient)
    const filters: IInboxFilters = yield select(getSmsFilters)

    const source: CancelTokenSource = action.payload

    const response: AxiosResponse<{
      result: ICommonListModel<ISmsListItem>
    }> = yield call(getSmsMessages, {
      ...filters,
      clientId: activeClient.id,
      source,
    })

    yield put({
      type: SMS_MESSAGES_FETCH_REQUEST_SUCCESS,
      payload: response.data,
    })
  } catch (e) {
    if (!axios.isCancel(e))
      yield put({
        type: SMS_MESSAGES_FETCH_REQUEST_ERROR,
        payload: e,
      })
  }
}

function* fetchSms(action: SmsActionTypes) {
  try {
    const id = action.payload.id

    if (id === null) {
      yield put({
        type: SMS_RESET_CURRENT,
      })

      return
    }

    const message: AxiosResponse<ISmsModel> = yield call(
      getSms,
      id,
      action.payload.tokenSource
    )
    yield put({
      type: SMS_FETCH_REQUEST_SUCCESS,
      payload: message.data,
    })
  } catch (e) {
    if (!axios.isCancel(e))
      yield put({
        type: SMS_FETCH_REQUEST_ERROR,
        payload: e,
      })
  }
}

function* fetchMoreSmsMessages() {
  const activeClient: Client = yield select(getActiveClient)
  const filters: IInboxFilters = yield select(getSmsFilters)

  const response: AxiosResponse<{
    result: ICommonListModel<ISmsListItem>
  }> = yield call(getSmsMessages, {
    ...filters,
    clientId: activeClient.id,
  })

  try {
    yield put({
      type: SMS_FETCH_MORE_REQUEST_SUCCESS,
      payload: response.data,
    })
  } catch (e) {
    yield put({
      type: SMS_FETCH_MORE_REQUEST_ERROR,
      payload: e,
    })
  }
}

function* updateFilters() {
  yield put({ type: SMS_MESSAGES_FETCH_REQUESTED })
}

function* addHandledByToSms(action: SmsActionTypes) {
  try {
    const payload = action.payload as string[]

    const patchModel: IInboxPatchModel = {
      model: payload.map((item) => ({
        id: item,
        patchModel: [{ op: 'replace', path: '/IsProcessed', value: true }],
      })),
    }

    yield call(patchSms, patchModel)

    yield put({
      type: SMS_SET_HANDLED_BY_SUCCESS,
      payload: { messageIds: payload },
    })

    yield put({
      type: SMS_RESET_CURRENT,
      payload: { navigateFromCurrent: true },
    })

    yield put(
      addToasterMessage({
        text: `SMS-en${
          payload.length > 1 ? 'e' : ''
        } ble flyttet til mappen "Behandlet"`,
        type: 'success',
      })
    )
  } catch (e) {
    yield put(
      addToasterMessage({
        text: 'Det skjedde en feil ved markering av behandlet',
        type: 'danger',
      })
    )
  }
}

function* removeHandledByToSms(action: SmsActionTypes) {
  try {
    const patchModel: IInboxPatchModel = {
      model: [
        {
          id: action.payload,
          patchModel: [{ op: 'replace', path: '/IsProcessed', value: false }],
        },
      ],
    }

    yield call(patchSms, patchModel)
    yield put({
      type: SMS_REMOVE_HANDLED_BY_SUCCESS,
      payload: action.payload,
    })

    yield put({
      type: SMS_RESET_CURRENT,
      payload: { navigateFromCurrent: true },
    })

    yield put(
      addToasterMessage({
        text: 'SMS-en har blitt flyttet tilbake til sin opprinnelige mappe',
        type: 'success',
      })
    )
  } catch (e) {
    yield put(
      addToasterMessage({
        text: 'Det skjedde en feil ved fjerning av behandlet av',
        type: 'danger',
      })
    )
  }
}

function* deleteSmsMessages(action: SmsActionTypes) {
  try {
    const currentId: string = yield select(getCurrentSms)
    const ids: string[] = action.payload.ids

    const patchList: IInboxPatchSingle[] = []

    if (action.payload.softDelete) {
      ids.map((id) =>
        patchList.push({
          id,
          patchModel: [{ op: 'replace', path: '/isDeleted', value: true }],
        })
      )

      const payload: IInboxPatchModel = {
        model: patchList,
      }

      yield call(patchSms, payload)
    } else {
      if (ids.length > 1) yield call(deleteSmsList, ids)
      else yield call(deleteSms, ids[0])
    }

    yield put({
      type: SMS_DELETE_SUCCESS,
      payload: ids,
    })

    if (ids.length === 1 && currentId === ids[0])
      yield put({
        type: SMS_RESET_CURRENT,
        payload: { navigateFromCurrent: true },
      })

    yield put({ type: SMS_DELETESTATUS_RESET })
    yield put(closeModal())
  } catch (e) {
    yield put({
      type: SMS_DELETE_ERROR,
    })
  }
}

function* restoreSmsMessages(action: SmsActionTypes) {
  try {
    const ids: string[] = action.payload

    const patchList: IInboxPatchSingle[] = []

    ids.map((id) =>
      patchList.push({
        id,
        patchModel: [{ op: 'replace', path: '/isDeleted', value: false }],
      })
    )

    const payload: IInboxPatchModel = {
      model: patchList,
    }

    yield call(patchSms, payload)

    yield put({
      type: SMS_RESTORE_SUCCESS,
      payload: ids,
    })

    yield put(
      addToasterMessage({
        text: `SMS-en${ids.length > 1 ? 'e' : ''} ble gjenopprettet`,
        type: 'success',
      })
    )
    yield put({ type: SMS_DELETESTATUS_RESET })
  } catch (e) {
    yield put(
      addToasterMessage({
        text: 'Det skjedde en feil under gjenoppretting av SMS',
        type: 'danger',
      })
    )
  }
}

function* fetchSmsSilent(action: SmsActionTypes) {
  try {
    const activeId: string = yield select(getCurrentSms)

    if (!activeId || (action.payload?.id && action.payload.id !== activeId))
      return

    const { data }: AxiosResponse<ISmsModel> = yield call(getSms, activeId)

    yield put({
      type: SMS_FETCH_SILENT_SUCCESS,
      payload: {
        sms: data,
        onlyFetchLabels: action.payload?.onlyFetchLabels,
      },
    })
  } catch (e) {
    yield put({
      type: SMS_FETCH_SILENT_ERROR,
      payload: e,
    })
  }
}

function* fetchNewSmsSilently(action: SmsActionTypes) {
  try {
    const fetchForSection = action.payload.section
    const activeClient: Client = yield select(getActiveClient)
    const filters: IInboxFilters = yield select(getSmsFilters)

    if (filters?.queryStatus !== fetchForSection) return

    const response: AxiosResponse<{
      result: ICommonListModel<ISmsListItem>
    }> = yield call(getSmsMessages, {
      ...filters,
      clientId: activeClient.id,
      // Only fetch 5 newest, to avoid fetching too much in the background
      skip: 0,
      take: 5,
    })

    yield put({
      type: SMS_FETCH_NEW_SILENTLY_SUCCESS,
      payload: response.data.result || [],
    })
  } catch (e) {
    console.error(e)
  }
}

function* navigateFromCurrent(action: SmsActionTypes) {
  if (!action.payload?.navigateFromCurrent) return
  try {
    const section: InboxSectionEnum = yield select(getSmsSection)
    const converted = convertEnumToReadable(
      section || InboxSectionEnum.All
    ).toLowerCase()
    navigate.push(`/kommunikasjon/sms/${converted}`)
  } catch (e) {
    console.log(e)
  }
}

function* smsSaga() {
  yield takeEvery(SMS_MESSAGES_FETCH_REQUESTED, fetchSmsMessages)
  yield takeEvery(SMS_FETCH_MORE_REQUESTED, fetchMoreSmsMessages)
  yield takeEvery(SMS_SET_FILTERS, updateFilters)
  yield takeEvery(SMS_SET_CURRENT, fetchSms)
  yield takeEvery(SMS_SET_HANDLED_BY_REQUESTED, addHandledByToSms)
  yield takeEvery(SMS_REMOVE_HANDLED_BY_REQUESTED, removeHandledByToSms)
  yield takeEvery(SMS_DELETE_REQUESTED, deleteSmsMessages)
  yield takeEvery(SMS_RESTORE_REQUESTED, restoreSmsMessages)
  yield takeEvery(SMS_FETCH_SILENT_REQUESTED, fetchSmsSilent)
  yield takeEvery(SMS_FETCH_NEW_SILENTLY_REQUESTED, fetchNewSmsSilently)
  yield takeEvery(SMS_RESET_CURRENT, navigateFromCurrent)
}

export default smsSaga
